// See below.
const NICE_MOVEMENT_THRESHOLD_PIXELS = 2;

interface DragPositionState {
    position: number;
    bigDiff: number;
}

type MovementDirection = "POSITIVE" | "NEGATIVE" | "ZERO";

// This is used with a `DragPositionState` state so detect movement
// direction changes so that the designer allows sections/elements of
// different heights. It could also be used for horizontal movement.
//
// The `position` is the y (or x) position of the mouse when the hover
// event is received (`monitor.getClientOffset()`).
//
// The `threshold` is a means to ignore too small movements. In
// practice `1` is a bit sensitive, and `3` is a bit too much.
//
// The returned value is `null` when the movement is not big enough.
// It is non `null` otherwise, but could be "ZERO" to handle the case
// of a mouse entering the component rect. The value indicates the
// direction, `position`-wise, of the movement.
function getBigMovementDirection(
    position: number | null,
    thresholdPixels: number,
    state: DragPositionState | null,
    setState: React.Dispatch<React.SetStateAction<DragPositionState | null>>
): MovementDirection | null {
    let isBig: boolean;
    let bigDiff = state?.bigDiff || 0;
    if (position !== null && state) {
        const diff = position - state.position;
        isBig = Math.abs(diff) >= thresholdPixels;
        if (isBig) {
            bigDiff = diff;
        }
    } else {
        isBig = true;
    }
    setState(
        position !== null
            ? {
                  position,
                  bigDiff,
              }
            : null
    );
    if (isBig) {
        if (bigDiff > 0) {
            return "POSITIVE";
        } else if (bigDiff < 0) {
            return "NEGATIVE";
        } else {
            return "ZERO";
        }
    } else {
        return null;
    }
}

// Despite of the `getBigMovementDirection` trick, some hover events
// are fired in random order (it seems to depend on the GC).
//
// So this is a helper to check if the mouse direction is the same as
// the change we are about to apply.
function isSameMovementDirection(
    oldValue: number,
    newValue: number,
    direction: MovementDirection
): boolean {
    if (
        // The mouse has probably hovered the element for the first time of its lifecycle,
        // let's assume we need to actually call the hover handler.
        direction === "ZERO" ||
        // Increasing value
        (direction === "POSITIVE" && newValue > oldValue) ||
        // Decreasing value
        (direction === "NEGATIVE" && newValue < oldValue)
    ) {
        return true;
    } else {
        return false;
    }
}

export {
    NICE_MOVEMENT_THRESHOLD_PIXELS,
    type DragPositionState,
    type MovementDirection,
    getBigMovementDirection,
    isSameMovementDirection,
};
